// Página PurchaseOrderDetail - Detalle completo de un pedido de compra
// Muestra toda la información del pedido y permite gestionar pagos, recepciones y finalización

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getPurchaseOrder, updatePurchaseOrderStatus, registerReceipt, completePurchaseOrder, deletePurchaseOrder } from '../../services/purchaseOrders.service'
import { productsService } from '../../services/firestore.service'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency, formatDate } from '../../utils/helpers'
import { getCategories } from '../../services/categories.service'
import { getImageUrl } from '../../services/cloudinary.service'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import PurchaseOrderStatusBadge from '../../components/purchaseOrders/PurchaseOrderStatusBadge'
import PaymentProgressBar from '../../components/purchaseOrders/PaymentProgressBar'
import ReceiptProgressBar from '../../components/purchaseOrders/ReceiptProgressBar'
import RatingStars from '../../components/purchaseOrders/RatingStars'
import './PurchaseOrderDetail.css'

/**
 * Página PurchaseOrderDetail
 * Muestra el detalle completo de un pedido de compra
 */
function PurchaseOrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, userData } = useAuth()
  
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('info')
  const [categories, setCategories] = useState([])
  
  // Estados para modales y formularios
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [showReceiptDialog, setShowReceiptDialog] = useState(false)
  const [receiptData, setReceiptData] = useState({})
  const [updateStock, setUpdateStock] = useState(true)
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)
  const [ratings, setRatings] = useState({})
  const [ratingNotes, setRatingNotes] = useState({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [pendingPriceUpdates, setPendingPriceUpdates] = useState([])
  
  const [notification, setNotification] = useState(null)

  const loadCategories = useCallback(async () => {
    try {
      const cats = await getCategories()
      setCategories(cats)
    } catch (error) {
      console.error('Error cargando categorías:', error)
    }
  }, [])

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const orderData = await getPurchaseOrder(id)
      
      if (!orderData) {
        setError('Pedido de compra no encontrado')
        return
      }
      
      setOrder(orderData)
      
      // Inicializar datos de recepción
      const receiptInit = {}
      orderData?.suppliers?.forEach(supplier => {
        supplier.items?.forEach(item => {
          receiptInit[`${supplier.supplierId}-${item.itemId}`] = {
            receivedQuantity: item.receivedQuantity || 0,
            defectiveQuantity: item.defectiveQuantity || 0
          }
        })
      })
      setReceiptData(receiptInit)
      
      // Inicializar ratings
      const ratingsInit = {}
      const ratingNotesInit = {}
      orderData?.suppliers?.forEach(supplier => {
        ratingsInit[supplier.supplierId] = supplier.rating || 0
        ratingNotesInit[supplier.supplierId] = supplier.ratingNotes || ''
      })
      setRatings(ratingsInit)
      setRatingNotes(ratingNotesInit)
    } catch (err) {
      console.error('Error cargando pedido de compra:', err)
      setError('Error al cargar el pedido de compra')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchOrder()
    loadCategories()
  }, [fetchOrder, loadCategories])

  const handleStatusChange = (status) => {
    setNewStatus(status)
    
    // Si se está confirmando, verificar si hay productos para actualizar
    if (status === 'confirmed' && order) {
      const productsToUpdate = []
      order.suppliers?.forEach(supplier => {
        supplier.items?.forEach(item => {
          if (item.type === 'product' && item.updateProductPrice && item.finalPrice) {
            const finalPrice = parseFloat(item.finalPrice)
            const currentPrice = item.currentProductPrice || 0
            if (finalPrice !== currentPrice) {
              productsToUpdate.push({
                productId: item.productId,
                productName: item.name,
                currentPrice,
                newPrice: finalPrice
              })
            }
          }
        })
      })
      
      if (productsToUpdate.length > 0) {
        setPendingPriceUpdates(productsToUpdate)
      }
    }
    
    setShowStatusDialog(true)
  }

  const confirmStatusChange = async () => {
    if (!order || !newStatus) return

    try {
      // Si se está confirmando la orden, actualizar precios de productos configurados
      if (newStatus === 'confirmed' && pendingPriceUpdates.length > 0) {
        await updateProductPricesOnConfirm(order, pendingPriceUpdates)
      }

      await updatePurchaseOrderStatus(
        order.id, 
        newStatus, 
        user?.uid || null, 
        userData?.name || userData?.displayName || user?.email || 'Sistema'
      )
      setOrder(prev => ({ ...prev, status: newStatus }))
      setNotification({
        type: 'success',
        message: `Estado del pedido actualizado a "${getStatusLabel(newStatus)}"`
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error actualizando estado:', err)
      setNotification({
        type: 'error',
        message: err.message || 'Error al actualizar el estado del pedido'
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setShowStatusDialog(false)
      setNewStatus('')
      setPendingPriceUpdates([])
    }
  }

  // Actualizar precios de productos cuando se confirma la orden
  const updateProductPricesOnConfirm = async (orderData, productsToUpdate) => {
    try {
      // Actualizar precios si hay productos para actualizar
      if (productsToUpdate && productsToUpdate.length > 0) {
        for (const productUpdate of productsToUpdate) {
          await productsService.updatePrice(
            productUpdate.productId,
            productUpdate.newPrice,
            {
              userId: user?.uid || null,
              userName: userData?.name || userData?.displayName || user?.email || 'Sistema',
              reason: `Actualización desde orden de compra ${orderData.orderNumber} (confirmada)`,
              purchaseOrderId: orderData.id,
              notes: `Precio actualizado al confirmar orden de compra. Precio anterior: ${formatCurrency(productUpdate.currentPrice)}`
            }
          )
        }
        
        setNotification({
          type: 'success',
          message: `${productsToUpdate.length} producto(s) actualizado(s) correctamente al confirmar la orden`
        })
        setTimeout(() => setNotification(null), 3000)
      }
    } catch (error) {
      console.error('Error actualizando precios al confirmar:', error)
      throw new Error(`Error al actualizar precios: ${error.message || 'Error desconocido'}`)
    }
  }

  const handleDelete = () => {
    setShowDeleteDialog(true)
  }

  const confirmDelete = async () => {
    if (!order) return

    try {
      await deletePurchaseOrder(order.id)
      setNotification({
        type: 'success',
        message: 'Pedido eliminado correctamente'
      })
      setTimeout(() => {
        navigate('/admin/pedidos-compra')
      }, 1500)
    } catch (err) {
      console.error('Error eliminando pedido:', err)
      setNotification({
        type: 'error',
        message: err.message || 'Error al eliminar el pedido'
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setShowDeleteDialog(false)
    }
  }


  const handleRegisterReceipt = (supplier) => {
    setSelectedSupplier(supplier)
    setShowReceiptDialog(true)
  }

  const confirmReceipt = async () => {
    if (!order || !selectedSupplier) return

    try {
      const receivedItems = []
      selectedSupplier.items.forEach(item => {
        const key = `${selectedSupplier.supplierId}-${item.itemId}`
        const receipt = receiptData[key]
        if (receipt && receipt.receivedQuantity > 0) {
          receivedItems.push({
            itemId: item.itemId,
            receivedQuantity: receipt.receivedQuantity,
            defectiveQuantity: receipt.defectiveQuantity || 0
          })
        }
      })

      if (receivedItems.length === 0) {
        throw new Error('Debe ingresar al menos una cantidad recibida')
      }

      await registerReceipt(
        order.id,
        selectedSupplier.supplierId,
        receivedItems,
        updateStock,
        user?.uid,
        userData?.name || user?.email
      )

      await fetchOrder() // Recargar datos
      setNotification({
        type: 'success',
        message: 'Recepción de productos registrada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error registrando recepción:', err)
      setNotification({
        type: 'error',
        message: err.message || 'Error al registrar la recepción'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setShowReceiptDialog(false)
      setSelectedSupplier(null)
    }
  }

  const handleComplete = () => {
    setShowCompleteDialog(true)
  }

  const confirmComplete = async () => {
    if (!order) return

    try {
      // Validar que todos los proveedores tengan rating
      const suppliersWithoutRating = order.suppliers.filter(s => !ratings[s.supplierId] || ratings[s.supplierId] === 0)
      if (suppliersWithoutRating.length > 0) {
        throw new Error('Debe calificar a todos los proveedores antes de completar el pedido')
      }

      const supplierRatings = {}
      order.suppliers.forEach(supplier => {
        supplierRatings[supplier.supplierId] = {
          rating: ratings[supplier.supplierId],
          notes: ratingNotes[supplier.supplierId] || null
        }
      })

      await completePurchaseOrder(
        order.id,
        supplierRatings,
        user?.uid,
        userData?.name || user?.email
      )

      await fetchOrder() // Recargar datos
      setNotification({
        type: 'success',
        message: 'Pedido de compra completado correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error completando pedido:', err)
      setNotification({
        type: 'error',
        message: err.message || 'Error al completar el pedido'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setShowCompleteDialog(false)
    }
  }

  const updateReceiptQuantity = (supplierId, itemId, field, value) => {
    const key = `${supplierId}-${itemId}`
    setReceiptData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: parseInt(value) || 0
      }
    }))
  }

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      partially_paid: 'Pago Parcial',
      paid: 'Pagado',
      partially_received: 'Recepción Parcial',
      received: 'Recibido',
      completed: 'Completado',
      cancelled: 'Cancelado'
    }
    return labels[status] || status
  }

  const getItemTypeLabel = (item) => {
    return item.type === 'supply' ? 'Insumo' : 'Producto'
  }

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Efectivo',
      transfer: 'Transferencia',
      check: 'Cheque',
      credit_card: 'Tarjeta de Crédito',
      debit_card: 'Tarjeta de Débito',
      other: 'Otro'
    }
    return methods[method] || method
  }

  const groupItemsByCategory = (items) => {
    const grouped = {}
    items.forEach(item => {
      // Intentar obtener el nombre de la categoría desde el item, o buscarlo en las categorías cargadas
      let categoryName = item.category
      
      // Si no tiene nombre de categoría pero tiene categoryId, buscarlo en las categorías
      if (!categoryName && item.categoryId) {
        const category = categories.find(c => c.id === item.categoryId)
        categoryName = category ? category.name : 'Sin categoría'
      }
      
      // Si es un insumo, usar "Insumos" como categoría
      if (!categoryName && item.type === 'supply') {
        categoryName = 'Insumos'
      }
      
      // Si aún no tiene categoría, usar "Sin categoría"
      if (!categoryName) {
        categoryName = 'Sin categoría'
      }
      
      if (!grouped[categoryName]) {
        grouped[categoryName] = []
      }
      grouped[categoryName].push(item)
    })
    
    // Ordenar las categorías alfabéticamente, pero poner "Sin categoría" al final
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      if (a === 'Sin categoría') return 1
      if (b === 'Sin categoría') return -1
      if (a === 'Insumos') return -1
      if (b === 'Insumos') return 1
      return a.localeCompare(b)
    })
    
    const sortedGrouped = {}
    sortedCategories.forEach(cat => {
      sortedGrouped[cat] = grouped[cat]
    })
    
    return sortedGrouped
  }

  if (loading) {
    return (
      <div className="purchase-order-detail">
        <div className="loading">Cargando pedido de compra...</div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="purchase-order-detail">
        <div className="error-message">
          {error || 'Pedido de compra no encontrado'}
          <Button onClick={() => navigate('/admin/pedidos-compra')} variant="secondary">
            Volver a Pedidos de Compra
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="purchase-order-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <Link to="/admin/pedidos-compra" className="back-link">
            ← Volver a Pedidos de Compra
          </Link>
          <h1>Pedido de Compra: {order.orderNumber}</h1>
          <div className="order-meta">
            <span>Fecha: {order.createdAt ? formatDate(order.createdAt) : 'No disponible'}</span>
            <PurchaseOrderStatusBadge status={order.status} />
          </div>
        </div>
        <div className="header-right">
          {order.status === 'pending' && (
            <Link to={`/admin/pedidos-compra/${id}/editar`}>
              <Button variant="secondary">
                ✏️ Editar Pedido
              </Button>
            </Link>
          )}
          {order.status !== 'completed' && order.status !== 'cancelled' && (
            <>
              <Button
                variant="secondary"
                onClick={() => handleStatusChange('confirmed')}
                disabled={order.status === 'confirmed'}
              >
                ✓ Confirmar
              </Button>
              <Button
                variant="danger"
                onClick={() => handleStatusChange('cancelled')}
                disabled={order.status === 'cancelled'}
              >
                ✕ Cancelar
              </Button>
              {order.status === 'pending' && (
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  style={{ marginLeft: '0.5rem' }}
                >
                  🗑️ Eliminar
                </Button>
              )}
              {order.status === 'received' && (
                <Button
                  variant="primary"
                  onClick={handleComplete}
                >
                  ✓ Completar Pedido
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Resumen de Totales */}
      <div className="order-summary">
        <div className="summary-card">
          <h3>Total Items</h3>
          <p className="summary-value">{order.totalItems || 0}</p>
        </div>
        <div className="summary-card">
          <h3>Subtotal</h3>
          <p className="summary-value">{formatCurrency(order.subtotal || 0)}</p>
        </div>
        <div className="summary-card">
          <h3>Impuestos</h3>
          <p className="summary-value">{formatCurrency(order.taxes || 0)}</p>
        </div>
        <div className="summary-card">
          <h3>Envío</h3>
          <p className="summary-value">{formatCurrency(order.shipping || 0)}</p>
        </div>
        <div className="summary-card highlight">
          <h3>Total</h3>
          <p className="summary-value">{formatCurrency(order.total || 0)}</p>
        </div>
        <div className="summary-card">
          <h3>Total Pagado</h3>
          <p className="summary-value">{formatCurrency(order.totalPaid || 0)}</p>
          <PaymentProgressBar
            paidAmount={order.totalPaid || 0}
            totalAmount={order.total || 0}
            showLabel={false}
          />
        </div>
        <div className="summary-card">
          <h3>Total Recibido</h3>
          <p className="summary-value">{order.totalReceived || 0} / {order.totalItems || 0}</p>
          <ReceiptProgressBar
            receivedQuantity={order.totalReceived || 0}
            totalQuantity={order.totalItems || 0}
            showLabel={false}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          📋 Información
        </button>
        <button
          className={`tab-button ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          📦 Productos/Insumos
        </button>
        <button
          className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          💰 Pagos
        </button>
        <button
          className={`tab-button ${activeTab === 'receipt' ? 'active' : ''}`}
          onClick={() => setActiveTab('receipt')}
        >
          📥 Recepción
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📜 Historial
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Tab: Información */}
        {activeTab === 'info' && (
          <div className="info-tab">
            <div className="info-grid">
              <div className="info-card">
                <h3>Información General</h3>
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-label">Número de Pedido:</span>
                    <span className="info-value">{order.orderNumber}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Estado:</span>
                    <span className="info-value">
                      <PurchaseOrderStatusBadge status={order.status} />
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Fecha de Creación:</span>
                    <span className="info-value">
                      {order.createdAt ? formatDate(order.createdAt) : 'No disponible'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Fecha Esperada de Entrega:</span>
                    <span className="info-value">
                      {order.expectedDeliveryDate ? formatDate(order.expectedDeliveryDate) : 'No especificada'}
                    </span>
                  </div>
                  {order.confirmedAt && (
                    <div className="info-item">
                      <span className="info-label">Fecha de Confirmación:</span>
                      <span className="info-value">{formatDate(order.confirmedAt)}</span>
                    </div>
                  )}
                  {order.completedAt && (
                    <div className="info-item">
                      <span className="info-label">Fecha de Finalización:</span>
                      <span className="info-value">{formatDate(order.completedAt)}</span>
                    </div>
                  )}
                  {order.notes && (
                    <div className="info-item">
                      <span className="info-label">Notas:</span>
                      <span className="info-value">{order.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="info-card">
                <h3>Proveedores ({order.suppliers?.length || 0})</h3>
                <div className="suppliers-list">
                  {order.suppliers?.map((supplier, index) => (
                    <div key={supplier.supplierId || index} className="supplier-card">
                      <div className="supplier-header">
                        <h4>{supplier.supplierName}</h4>
                        <span className="supplier-subtotal">{formatCurrency(supplier.subtotal || 0)}</span>
                      </div>
                      <div className="supplier-details">
                        <div className="detail-row">
                          <span>Items: {supplier.items?.length || 0}</span>
                          <span>Total Items: {supplier.items?.reduce((sum, item) => sum + (item.quantity || 0), 0)}</span>
                        </div>
                        <div className="detail-row">
                          <span>Pagado: {formatCurrency(supplier.paidAmount || 0)}</span>
                          <span>Pendiente: {formatCurrency((supplier.subtotal || 0) - (supplier.paidAmount || 0))}</span>
                        </div>
                        {supplier.rating && (
                          <div className="detail-row">
                            <span>Calificación:</span>
                            <RatingStars rating={supplier.rating} readOnly size="small" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Productos/Insumos */}
        {activeTab === 'items' && (
          <div className="items-tab">
            {order.suppliers?.map((supplier, supplierIndex) => {
              const itemsByCategory = groupItemsByCategory(supplier.items || [])
              return (
                <div key={supplier.supplierId || supplierIndex} className="supplier-section">
                  <div className="supplier-section-header">
                    <h3>{supplier.supplierName}</h3>
                    <span className="supplier-total">{formatCurrency(supplier.subtotal || 0)}</span>
                  </div>

                  {Object.entries(itemsByCategory).map(([category, items]) => (
                    <div key={category} className="category-section">
                      <h4 className="category-title">{category}</h4>
                      <div className="items-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Tipo</th>
                              <th>Nombre</th>
                              <th>Cantidad</th>
                              <th>Precio Compra</th>
                              {items.some(item => item.type === 'product' && item.updateProductPrice && item.finalPrice) && (
                                <>
                                  <th>Precio Actual</th>
                                  <th>Precio Nuevo</th>
                                  <th>Estado</th>
                                </>
                              )}
                              <th>Total</th>
                              <th>Recibido</th>
                              <th>Defectuoso</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, itemIndex) => {
                              const showPriceComparison = item.type === 'product' && item.updateProductPrice && item.finalPrice
                              const currentPrice = item.currentProductPrice || 0
                              const newPrice = parseFloat(item.finalPrice || 0)
                              const priceChanged = currentPrice !== newPrice
                              const changePercent = currentPrice > 0 
                                ? ((newPrice - currentPrice) / currentPrice * 100).toFixed(1)
                                : '0'
                              const isSignificantChange = Math.abs(parseFloat(changePercent)) > 20
                              
                              return (
                                <tr key={item.itemId || itemIndex} className={showPriceComparison && isSignificantChange ? 'significant-price-change' : ''}>
                                  <td>{getItemTypeLabel(item)}</td>
                                  <td>{item.name}</td>
                                  <td>{item.quantity}</td>
                                  <td>{formatCurrency(item.unitPrice || 0)}</td>
                                  {showPriceComparison && (
                                    <>
                                      <td className="current-price-cell">
                                        {formatCurrency(currentPrice)}
                                      </td>
                                      <td className={`new-price-cell ${priceChanged ? (newPrice > currentPrice ? 'price-increase' : 'price-decrease') : ''}`}>
                                        {formatCurrency(newPrice)}
                                      </td>
                                      <td className="price-status-cell">
                                        {priceChanged ? (
                                          <span className={`price-change-badge ${isSignificantChange ? 'significant' : ''}`}>
                                            {newPrice > currentPrice ? '↑' : '↓'} {changePercent}%
                                            {isSignificantChange && <span className="warning-icon">⚠️</span>}
                                          </span>
                                        ) : (
                                          <span className="price-unchanged">Sin cambios</span>
                                        )}
                                      </td>
                                    </>
                                  )}
                                  <td>{formatCurrency(item.totalPrice || 0)}</td>
                                  <td>{item.receivedQuantity || 0}</td>
                                  <td>{item.defectiveQuantity || 0}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )}

        {/* Tab: Pagos */}
        {activeTab === 'payments' && (
          <div className="payments-tab">
            {order.status === 'pending' ? (
              <div className="info-message">
                <p>⚠️ Los pagos solo pueden registrarse después de confirmar la orden.</p>
                <p>Confirma la orden para habilitar la carga de pagos.</p>
              </div>
            ) : (
              order.suppliers?.map((supplier, index) => (
                <div key={supplier.supplierId || index} className="payment-section">
                <div className="payment-section-header">
                  <div>
                    <h3>{supplier.supplierName}</h3>
                    <p className="payment-subtotal">Subtotal: {formatCurrency(supplier.subtotal || 0)}</p>
                  </div>
                  <div className="payment-actions">
                    <PaymentProgressBar
                      paidAmount={supplier.paidAmount || 0}
                      totalAmount={supplier.subtotal || 0}
                      showLabel={true}
                    />
                    {order.status !== 'completed' && order.status !== 'cancelled' && order.status !== 'pending' && (
                      <Link to={`/admin/pedidos-compra/${id}/pago/${supplier.supplierId}`}>
                        <Button
                          variant="primary"
                          disabled={(supplier.paidAmount || 0) >= (supplier.subtotal || 0)}
                        >
                          + Registrar Pago
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                {supplier.paymentHistory && supplier.paymentHistory.length > 0 ? (
                  <div className="payment-history">
                    <h4>Historial de Pagos</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Monto</th>
                          <th>Método</th>
                          <th>Notas</th>
                          <th>Comprobantes</th>
                          <th>Registrado por</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplier.paymentHistory.map((payment, pIndex) => (
                          <tr key={pIndex}>
                            <td>{formatDate(payment.paymentDate)}</td>
                            <td>{formatCurrency(payment.amount)}</td>
                            <td>{getPaymentMethodLabel(payment.paymentMethod)}</td>
                            <td>{payment.notes || '-'}</td>
                            <td>
                              {payment.receiptImages && payment.receiptImages.length > 0 ? (
                                <div className="receipt-images">
                                  {payment.receiptImages.map((image, imgIndex) => (
                                    <a
                                      key={imgIndex}
                                      href={getImageUrl(image.fileId || image.url, { width: 1200 })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="receipt-image-link"
                                      title="Ver comprobante"
                                    >
                                      <img
                                        src={getImageUrl(image.fileId || image.url, { width: 100, height: 100, crop: 'fill' })}
                                        alt={`Comprobante ${imgIndex + 1}`}
                                        className="receipt-thumbnail"
                                      />
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{payment.createdByName || payment.createdBy || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">No hay pagos registrados</p>
                )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Tab: Recepción */}
        {activeTab === 'receipt' && (
          <div className="receipt-tab">
            {order.suppliers?.map((supplier, supplierIndex) => (
              <div key={supplier.supplierId || supplierIndex} className="receipt-section">
                <div className="receipt-section-header">
                  <div>
                    <h3>{supplier.supplierName}</h3>
                    <ReceiptProgressBar
                      receivedQuantity={supplier.items?.reduce((sum, item) => sum + (item.receivedQuantity || 0), 0) || 0}
                      totalQuantity={supplier.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                      showLabel={true}
                    />
                  </div>
                  {order.status !== 'completed' && order.status !== 'cancelled' && (
                    <Button
                      variant="primary"
                      onClick={() => handleRegisterReceipt(supplier)}
                    >
                      + Registrar Recepción
                    </Button>
                  )}
                </div>

                <div className="receipt-items">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Tipo</th>
                        <th>Cantidad Solicitada</th>
                        <th>Recibido</th>
                        <th>Defectuoso</th>
                        <th>Pendiente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplier.items?.map((item, itemIndex) => {
                        const pending = (item.quantity || 0) - (item.receivedQuantity || 0)
                        return (
                          <tr key={item.itemId || itemIndex}>
                            <td>{item.name}</td>
                            <td>{getItemTypeLabel(item)}</td>
                            <td>{item.quantity}</td>
                            <td className={item.receivedQuantity && item.receivedQuantity > 0 ? 'received' : ''}>
                              {item.receivedQuantity || 0}
                            </td>
                            <td className={item.defectiveQuantity && item.defectiveQuantity > 0 ? 'defective' : ''}>
                              {item.defectiveQuantity || 0}
                            </td>
                            <td>{pending}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab: Historial */}
        {activeTab === 'history' && (
          <div className="history-tab">
            {order.history && Object.keys(order.history).length > 0 ? (
              <div className="history-timeline">
                {Object.values(order.history)
                  .sort((a, b) => {
                    const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp)
                    const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp)
                    return bTime - aTime
                  })
                  .map((entry, index) => (
                    <div key={index} className="history-entry">
                      <div className="history-date">{formatDate(entry.timestamp)}</div>
                      <div className="history-content">
                        <div className="history-action">{entry.action}</div>
                        <div className="history-description">{entry.description}</div>
                        <div className="history-meta">
                          <span>Estado: <PurchaseOrderStatusBadge status={entry.status} /></span>
                          {entry.userName && <span>Por: {entry.userName}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="no-data">No hay historial disponible</p>
            )}
          </div>
        )}
      </div>

      {/* Modales */}
      {/* Modal: Cambiar Estado */}
      <ConfirmDialog
        isOpen={showStatusDialog}
        onClose={() => {
          setShowStatusDialog(false)
          setNewStatus('')
          setPendingPriceUpdates([])
        }}
        onConfirm={confirmStatusChange}
        title={newStatus === 'confirmed' ? "Confirmar Pedido de Compra" : "Cambiar Estado del Pedido"}
        message={
          newStatus === 'confirmed' && pendingPriceUpdates.length > 0 ? (
            <div className="confirm-status-content">
              <p>¿Estás seguro de confirmar este pedido de compra?</p>
              <p className="confirm-warning-text">
                ⚠️ Al confirmar, se actualizarán los precios de los siguientes productos:
              </p>
              <table className="price-update-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Precio Actual</th>
                    <th>Nuevo Precio</th>
                    <th>Cambio</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingPriceUpdates.map((update, index) => {
                    const change = update.newPrice - update.currentPrice
                    const changePercent = update.currentPrice > 0 
                      ? ((change / update.currentPrice) * 100).toFixed(1)
                      : '0'
                    const isSignificant = Math.abs(changePercent) > 20
                    
                    return (
                      <tr key={index} className={isSignificant ? 'significant-change' : ''}>
                        <td>{update.productName}</td>
                        <td>{formatCurrency(update.currentPrice)}</td>
                        <td>{formatCurrency(update.newPrice)}</td>
                        <td className={change >= 0 ? 'positive' : 'negative'}>
                          {change >= 0 ? '+' : ''}{formatCurrency(change)} ({changePercent}%)
                          {isSignificant && <span className="warning-badge">⚠️</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="confirm-info-text">
                Una vez confirmada, la orden no podrá editarse y se habilitará la carga de pagos.
              </p>
            </div>
          ) : (
            `¿Estás seguro de cambiar el estado del pedido a "${getStatusLabel(newStatus)}"?`
          )
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        variant={newStatus === 'confirmed' && pendingPriceUpdates.length > 0 ? 'warning' : 'default'}
      />

      {/* Dialog: Confirmar Eliminación */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Eliminar Pedido de Compra"
        message={
          <div>
            <p>¿Estás seguro de que deseas eliminar este pedido de compra?</p>
            <p className="warning-text" style={{ marginTop: '1rem', padding: '1rem', background: '#fff3cd', borderLeft: '4px solid #ffc107', borderRadius: '4px', color: '#856404' }}>
              ⚠️ Esta acción no se puede deshacer. El pedido será eliminado permanentemente.
            </p>
            <p style={{ marginTop: '1rem' }}><strong>Pedido:</strong> {order?.orderNumber}</p>
          </div>
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
      />

      {/* Modal: Registrar Recepción */}
      <ConfirmDialog
        isOpen={showReceiptDialog}
        onClose={() => setShowReceiptDialog(false)}
        onConfirm={confirmReceipt}
        title={`Registrar Recepción - ${selectedSupplier?.supplierName || ''}`}
        message={
          <div className="receipt-form">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={updateStock}
                  onChange={(e) => setUpdateStock(e.target.checked)}
                />
                Actualizar stock automáticamente
              </label>
            </div>
            <div className="receipt-items-form">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Solicitado</th>
                    <th>Recibido</th>
                    <th>Defectuoso</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSupplier?.items?.map((item, itemIndex) => {
                    const key = `${selectedSupplier.supplierId}-${item.itemId}`
                    const currentReceipt = receiptData[key] || { receivedQuantity: 0, defectiveQuantity: 0 }
                    const pending = (item.quantity || 0) - (item.receivedQuantity || 0)
                    return (
                      <tr key={item.itemId || itemIndex}>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={pending}
                            value={currentReceipt.receivedQuantity || 0}
                            onChange={(e) => updateReceiptQuantity(selectedSupplier.supplierId, item.itemId, 'receivedQuantity', e.target.value)}
                            disabled={pending <= 0}
                          />
                          <small>Pendiente: {pending}</small>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={currentReceipt.receivedQuantity || 0}
                            value={currentReceipt.defectiveQuantity || 0}
                            onChange={(e) => updateReceiptQuantity(selectedSupplier.supplierId, item.itemId, 'defectiveQuantity', e.target.value)}
                            disabled={!currentReceipt.receivedQuantity || currentReceipt.receivedQuantity <= 0}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        }
        confirmText="Registrar Recepción"
        cancelText="Cancelar"
      />

      {/* Modal: Completar Pedido */}
      <ConfirmDialog
        isOpen={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
        onConfirm={confirmComplete}
        title="Completar Pedido de Compra"
        message={
          <div className="complete-form">
            <p>Califica a cada proveedor antes de completar el pedido:</p>
            {order.suppliers?.map((supplier, index) => (
              <div key={supplier.supplierId || index} className="rating-item">
                <label>{supplier.supplierName}</label>
                <RatingStars
                  rating={ratings[supplier.supplierId] || 0}
                  onRatingChange={(rating) => setRatings({ ...ratings, [supplier.supplierId]: rating })}
                  readOnly={false}
                />
                <textarea
                  placeholder="Notas sobre este proveedor (opcional)"
                  value={ratingNotes[supplier.supplierId] || ''}
                  onChange={(e) => setRatingNotes({ ...ratingNotes, [supplier.supplierId]: e.target.value })}
                  rows="2"
                />
              </div>
            ))}
          </div>
        }
        confirmText="Completar Pedido"
        cancelText="Cancelar"
      />

      {/* Toast Notifications */}
      {notification && (
        <Toast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default PurchaseOrderDetail

